import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppBar, Box, IconButton, Toolbar, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { useQuery } from '@tanstack/react-query';

import { checkoutAPIService } from '~/api/checkout';
import CheckCircleIcon from '~/assets/icons/modal/check.svg?react';
import QRFullLogo from '~/assets/icons/sidebar-logo-new.svg?react';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { checkoutKeys } from '~/constants/queryKeys';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import { googleAnalytics, googleDataLayerPush } from '~/services/google-analytics-service';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { getAccountThunk } from '~/store/reducers/account/slice';
import { COLORS } from '~/styles/colors';

export default function SuccessPayment() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data } = useSelector(getLoggedInUser);

  const { data: subscriptionInfo } = useQuery({
    queryKey: [checkoutKeys.subscription],
    queryFn: () => checkoutAPIService.checkSubscription(),
    select: (response) => response.data,
  });

  useEffect(() => {
    if (subscriptionInfo?.id && subscriptionInfo.lastInvoice?.id) {
      googleDataLayerPush('event', 'purchase_success_page_web', {
        transaction_id: subscriptionInfo.lastInvoice.id,
        value: subscriptionInfo.lastInvoice.total,
        currency: subscriptionInfo.lastInvoice.currency?.toUpperCase(),
        items: [
          {
            item_name: subscriptionInfo.plan.product.name,
            price: subscriptionInfo.lastInvoice.total,
            quantity: 1,
          },
        ],
      });
    }
  }, [subscriptionInfo?.id]);

  useEffect(() => {
    dispatch(getAccountThunk());
  }, []);

  useEffect(() => {
    if (data?.id) {
      googleAnalytics.events.successPaymentPage({ userId: data.id, email: data.email });
    }
  }, [data?.id]);

  return (
    <Box sx={{ height: '90vh', width: '100%' }}>
      <AppBar position="static" sx={{ backgroundColor: COLORS.white }}>
        <Toolbar variant="dense">
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate(PATH.QR.MAIN)}
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'inherit',
                },
              }}
            >
              <QRFullLogo />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={() => navigate(PATH.SETTINGS)}>
              <Typography color={COLORS.black08} fontSize={14} sx={{ pr: 2, wordBreak: 'break-word' }}>
                {data.email || ''}
              </Typography>
              <Avatar src="/broken-image.jpg" sx={{ width: 24, height: 24 }} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Box
          sx={{
            boxShadow: '0px 0px 20px 0px #0000000D',
            backgroundColor: COLORS.white,
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
            maxWidth: '512px',
            width: '100%',
            m: 1,
            padding: '40px 37px',
          }}
        >
          <CheckCircleIcon />
          <Text sx={{ marginTop: '32px' }} variant="h2">
            {t('checkout:successfullPayment')}
          </Text>
          <Text variant="input15" sx={{ color: COLORS.grey400, mt: 1 }}>
            {t('checkout:successfullPaymentText')}
          </Text>

          <Button
            onClick={() => navigate(PATH.QR.MAIN)}
            variant="contained"
            sx={{ backgroundColor: COLORS.purple, mt: '24px', width: 'fit-content', height: '40px' }}
          >
            {t('checkout:continue')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
